// frontend/modules/customer-notification/components/common/CustomerSegmentSelector.tsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Radio, DatePicker, Space, Button, Badge, Row, Col, Typography, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

interface CustomerSegmentSelectorProps {
    onChange: (filters: Record<string, any>) => void;
    className?: string;
}

interface FilterCondition {
    field: string;
    operator: string;
    value: any;
    id: number;
}

const CustomerSegmentSelector: React.FC<CustomerSegmentSelectorProps> = ({ onChange, className }) => {
    const [conditions, setConditions] = useState<FilterCondition[]>([]);
    const [nextConditionId, setNextConditionId] = useState(1);
    
    // Field options for filtering
    const fieldOptions = [
        { label: 'Branch', value: 'branch' },
        { label: 'Gender', value: 'gender' },
        { label: 'Customer Type', value: 'custType' },
        { label: 'Group', value: 'groupCode' },
        { label: 'Area', value: 'areaCode' },
        { label: 'Has Active Loans', value: 'hasActiveLoans' },
        { label: 'Has Active Savings', value: 'hasActiveSavings' },
        { label: 'Registration Date', value: 'regDate' },
        { label: 'Preferred Language', value: 'preferredLanguage' }
    ];
    
    // Operator options based on field type
    const getOperatorOptions = (field: string) => {
        // Boolean fields
        if (['hasActiveLoans', 'hasActiveSavings'].includes(field)) {
            return [
                { label: 'Is', value: 'equals' }
            ];
        }
        
        // Date fields
        if (['regDate'].includes(field)) {
            return [
                { label: 'Before', value: 'before' },
                { label: 'After', value: 'after' },
                { label: 'Between', value: 'between' }
            ];
        }
        
        // String/enum fields
        return [
            { label: 'Equals', value: 'equals' },
            { label: 'Contains', value: 'contains' },
            { label: 'Not Equals', value: 'notEquals' }
        ];
    };
    
    // Get value input component based on field and operator
    const getValueComponent = (condition: FilterCondition) => {
        const { field, operator } = condition;
        
        // Boolean fields
        if (['hasActiveLoans', 'hasActiveSavings'].includes(field)) {
            return (
                <Radio.Group 
                    value={condition.value === undefined ? true : condition.value}
                    onChange={(e) => updateConditionValue(condition.id, e.target.value)}
                >
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                </Radio.Group>
            );
        }
        
        // Gender field
        if (field === 'gender') {
            return (
                <Select 
                    placeholder="Select gender"
                    value={condition.value}
                    onChange={(value) => updateConditionValue(condition.id, value)}
                    style={{ width: '100%' }}
                >
                    <Option value="M">Male</Option>
                    <Option value="F">Female</Option>
                </Select>
            );
        }
        
        // Preferred language field
        if (field === 'preferredLanguage') {
            return (
                <Select 
                    placeholder="Select language"
                    value={condition.value}
                    onChange={(value) => updateConditionValue(condition.id, value)}
                    style={{ width: '100%' }}
                >
                    <Option value="EN">English</Option>
                    <Option value="SW">Swahili</Option>
                </Select>
            );
        }
        
        // Branch field would typically be a dropdown with available branches
        if (field === 'branch') {
            return (
                <Select 
                    placeholder="Select branch"
                    value={condition.value}
                    onChange={(value) => updateConditionValue(condition.id, value)}
                    style={{ width: '100%' }}
                >
                    <Option value="1">Main Branch</Option>
                    <Option value="2">North Branch</Option>
                    <Option value="3">South Branch</Option>
                    <Option value="4">East Branch</Option>
                    <Option value="5">West Branch</Option>
                </Select>
            );
        }
        
        // Date fields
        if (field === 'regDate') {
            if (operator === 'between') {
                return (
                    <RangePicker 
                        value={condition.value}
                        onChange={(dates) => updateConditionValue(condition.id, dates)}
                        style={{ width: '100%' }}
                    />
                );
            } else {
                return (
                    <DatePicker 
                        value={condition.value}
                        onChange={(date) => updateConditionValue(condition.id, date)}
                        style={{ width: '100%' }}
                    />
                );
            }
        }
        
        // Default to text input
        return (
            <Input 
                value={condition.value}
                onChange={(e) => updateConditionValue(condition.id, e.target.value)}
                placeholder="Enter value"
            />
        );
    };
    
    // Add new condition
    const addCondition = () => {
        const newCondition: FilterCondition = {
            field: 'branch',
            operator: 'equals',
            value: '',
            id: nextConditionId
        };
        
        setConditions([...conditions, newCondition]);
        setNextConditionId(nextConditionId + 1);
    };
    
    // Remove condition
    const removeCondition = (id: number) => {
        setConditions(conditions.filter(condition => condition.id !== id));
    };
    
    // Update condition field
    const updateConditionField = (id: number, field: string) => {
        setConditions(conditions.map(condition => {
            if (condition.id === id) {
                // Reset value since field type might change
                return { ...condition, field, operator: getOperatorOptions(field)[0].value, value: undefined };
            }
            return condition;
        }));
    };
    
    // Update condition operator
    const updateConditionOperator = (id: number, operator: string) => {
        setConditions(conditions.map(condition => {
            if (condition.id === id) {
                // Reset value if operator type might change (e.g., between needs array value)
                return { ...condition, operator, value: undefined };
            }
            return condition;
        }));
    };
    
    // Update condition value
    const updateConditionValue = (id: number, value: any) => {
        setConditions(conditions.map(condition => {
            if (condition.id === id) {
                return { ...condition, value };
            }
            return condition;
        }));
    };
    
    // Convert conditions to filter object
    useEffect(() => {
        const filters: Record<string, any> = {};
        
        conditions.forEach(condition => {
            switch (condition.field) {
                case 'branch':
                case 'gender':
                case 'custType':
                case 'groupCode':
                case 'areaCode':
                case 'preferredLanguage':
                    // String/enum equality
                    if (condition.value) {
                        switch (condition.operator) {
                            case 'equals':
                                filters[condition.field] = condition.value;
                                break;
                            case 'notEquals':
                                filters[`${condition.field}Ne`] = condition.value;
                                break;
                            case 'contains':
                                filters[`${condition.field}Contains`] = condition.value;
                                break;
                        }
                    }
                    break;
                
                case 'hasActiveLoans':
                case 'hasActiveSavings':
                    // Boolean fields
                    if (condition.value !== undefined) {
                        filters[condition.field] = condition.value;
                    }
                    break;
                
                case 'regDate':
                    // Date fields
                    if (condition.value) {
                        if (condition.operator === 'between' && Array.isArray(condition.value)) {
                            if (condition.value[0]) {
                                filters.regDateFrom = condition.value[0].format('YYYY-MM-DD');
                            }
                            if (condition.value[1]) {
                                filters.regDateTo = condition.value[1].format('YYYY-MM-DD');
                            }
                        } else if (condition.operator === 'before') {
                            filters.regDateBefore = condition.value.format('YYYY-MM-DD');
                        } else if (condition.operator === 'after') {
                            filters.regDateAfter = condition.value.format('YYYY-MM-DD');
                        }
                    }
                    break;
            }
        });
        
        onChange(filters);
    }, [conditions, onChange]);
    
    return (
        <div className={className}>
            <div className="mb-4">
                <Text strong>Define customer segments for targeting:</Text>
                <Text className="ml-2 text-gray-500">
                    Add filters to narrow down the target audience
                </Text>
            </div>
            
            {conditions.length === 0 ? (
                <div className="text-center py-4 bg-gray-50 rounded mb-4">
                    <Text type="secondary">No filters added - notification will target all customers</Text>
                </div>
            ) : (
                <div className="mb-4">
                    {conditions.map((condition, index) => (
                        <div key={condition.id} className="mb-3 p-3 border rounded bg-gray-50">
                            <Row gutter={[12, 12]} align="middle">
                                <Col span={7}>
                                    <Select
                                        value={condition.field}
                                        onChange={(value) => updateConditionField(condition.id, value)}
                                        style={{ width: '100%' }}
                                    >
                                        {fieldOptions.map(option => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                                
                                <Col span={6}>
                                    <Select
                                        value={condition.operator}
                                        onChange={(value) => updateConditionOperator(condition.id, value)}
                                        style={{ width: '100%' }}
                                    >
                                        {getOperatorOptions(condition.field).map(option => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                                
                                <Col span={9}>
                                    {getValueComponent(condition)}
                                </Col>
                                
                                <Col span={2} className="text-right">
                                    <Button
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeCondition(condition.id)}
                                        danger
                                        type="text"
                                    />
                                </Col>
                            </Row>
                        </div>
                    ))}
                </div>
            )}
            
            <Button
                type="dashed"
                onClick={addCondition}
                block
                icon={<PlusOutlined />}
            >
                Add Filter
            </Button>
            
            {conditions.length > 0 && (
                <div className="mt-4">
                    <Badge 
                        count={conditions.length} 
                        style={{ backgroundColor: '#1890ff' }} 
                        className="mr-2" 
                    />
                    <Text type="secondary">
                        {conditions.length} {conditions.length === 1 ? 'filter' : 'filters'} applied
                    </Text>
                </div>
            )}
        </div>
    );
};

export default CustomerSegmentSelector;
